import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    IconButton,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import { useApiClient } from "../../components/useApiClient";
import useAuthToken from "../../functions/useAuthToken";
import TimeOffsetCheckboxGroup from "./ReminderOffsetsSelector";

import { PaymentMilestoneStatus } from "../../models/Payments";
import {
    PaymentScheduleBalanceMode,
    type DiaryPaymentMilestoneDto,
    type PaymentScheduleSummaryDto,
    type PaymentScheduleUpdateDto,
} from "../../models/PaymentUpdate";

type Props = {
    open: boolean;
    paymentScheduleId: number | null;
    artworkId: number;
    onClose: () => void;
    onUpdated: (schedule: PaymentScheduleSummaryDto) => void;
};

type EditableMilestoneRow = {
    milestoneId?: number | null;
    diaryItemId?: number | null;

    sequenceNumber: number;

    label: string;
    dueLocal: string;

    amount: number;
    amountPaid: number;

    originalStatus: PaymentMilestoneStatus;
    status: PaymentMilestoneStatus;

    canEditAmount: boolean;
    canEditDueDate: boolean;
    canDelete: boolean;
    canCancel: boolean;
    canRestore: boolean;

    rowVersion?: string | null;

    isNew: boolean;
};

function toDateOnly(value?: string | null): string {
    if (!value) return "";

    return value.substring(0, 10);
}

function toUtcIsoFromLocalDate(value: string): string | null {
    if (!value) return null;

    return new Date(`${value}T00:00:00Z`).toISOString();
}

function toMinorUnits(value: number): number {
    return Math.round((Number.isFinite(value) ? value : 0) * 100);
}

function fromMinorUnits(value: number): number {
    return value / 100;
}

function money(value: number): number {
    return fromMinorUnits(toMinorUnits(value));
}

function mapMilestone(
    milestone: DiaryPaymentMilestoneDto
): EditableMilestoneRow {
    return {
        milestoneId: milestone.milestoneId,
        diaryItemId: milestone.diaryItemId,

        sequenceNumber: milestone.sequenceNumber,

        label: milestone.label ?? "",
        dueLocal: toDateOnly(milestone.dueUtc),

        amount: milestone.amount,
        amountPaid: milestone.amountPaid,

        originalStatus: milestone.status,
        status: milestone.status,

        canEditAmount: milestone.canEditAmount,
        canEditDueDate: milestone.canEditDueDate,
        canDelete: milestone.canDelete,
        canCancel: milestone.canCancel,
        canRestore: milestone.canRestore,

        rowVersion: milestone.rowVersion,

        isNew: false,
    };
}

function getStatusLabel(
    status: PaymentMilestoneStatus
): string {
    switch (status) {
        case PaymentMilestoneStatus.Planned:
            return "Planned";

        case PaymentMilestoneStatus.PartPaid:
            return "Part paid";

        case PaymentMilestoneStatus.Paid:
            return "Paid";

        case PaymentMilestoneStatus.Cancelled:
            return "Cancelled";

        default:
            return "Unknown";
    }
}

function getStatusChipColor(
    status: PaymentMilestoneStatus
):
    | "default"
    | "primary"
    | "success"
    | "warning"
    | "error" {
    switch (status) {
        case PaymentMilestoneStatus.Paid:
            return "success";

        case PaymentMilestoneStatus.PartPaid:
            return "warning";

        case PaymentMilestoneStatus.Cancelled:
            return "default";

        default:
            return "primary";
    }
}

function extractErrorMessage(error: unknown): string {
    if (error instanceof Error) {
        return error.message;
    }

    if (typeof error !== "object" || error === null) {
        return "The payment schedule could not be updated.";
    }

    if (
        "detail" in error &&
        typeof error.detail === "string"
    ) {
        return error.detail;
    }

    if (
        "title" in error &&
        typeof error.title === "string"
    ) {
        return error.title;
    }

    if (
        "message" in error &&
        typeof error.message === "string"
    ) {
        return error.message;
    }

    return "The payment schedule could not be updated.";
}

export default function ArtworkPaymentScheduleEditDialog({
    open,
    paymentScheduleId,
    artworkId,
    onClose,
    onUpdated,
}: Props) {
    const getAccessToken = useAuthToken();

    const {
        get,
        putJson,
    } = useApiClient();

    const [schedule, setSchedule] =
        useState<PaymentScheduleSummaryDto | null>(null);

    const [title, setTitle] = useState("");
    const [currencyCode, setCurrencyCode] = useState("GBP");

    const [enteredTotalAmount, setEnteredTotalAmount] =
        useState(0);

    const [balanceMode, setBalanceMode] =
        useState<PaymentScheduleBalanceMode>(
            PaymentScheduleBalanceMode.UseEnteredScheduleTotal
        );

    const [visibility, setVisibility] = useState(1);

    const [reminders, setReminders] =
        useState<number[]>([]);

    const [milestones, setMilestones] =
        useState<EditableMilestoneRow[]>([]);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [loadError, setLoadError] =
        useState<string | null>(null);

    const [saveErrors, setSaveErrors] =
        useState<string[]>([]);

    const resetState = useCallback(() => {
        setSchedule(null);
        setTitle("");
        setCurrencyCode("GBP");
        setEnteredTotalAmount(0);

        setBalanceMode(
            PaymentScheduleBalanceMode.UseEnteredScheduleTotal
        );

        setVisibility(1);
        setReminders([]);
        setMilestones([]);

        setLoadError(null);
        setSaveErrors([]);
    }, []);

    const loadSchedule = useCallback(async () => {
        if (!paymentScheduleId) return;

        setLoading(true);
        setLoadError(null);
        setSaveErrors([]);

        try {
            const token = await getAccessToken();

            if (!token) {
                throw new Error(
                    "A valid access token could not be obtained."
                );
            }

            const result = await get(
                `payment-schedules/${paymentScheduleId}/edit`,
                token
            ) as PaymentScheduleSummaryDto;

            setSchedule(result);

            setTitle(result.title ?? "");
            setCurrencyCode(result.currencyCode ?? "GBP");
            setEnteredTotalAmount(result.totalAmount);
            setVisibility(result.visibility);

            setReminders(
                result.reminderOffsetsMinutes ?? []
            );

            setMilestones(
                (result.milestones ?? []).map(mapMilestone)
            );

            setBalanceMode(
                PaymentScheduleBalanceMode.UseEnteredScheduleTotal
            );
        } catch (error) {
            setLoadError(extractErrorMessage(error));
        } finally {
            setLoading(false);
        }
    }, [
        paymentScheduleId,
    ]);

    useEffect(() => {
        if (!open) {
            resetState();
            return;
        }

        void loadSchedule();
    }, [
        open,
        loadSchedule,
        resetState,
    ]);

    const activeMilestones = useMemo(
        () =>
            milestones.filter(
                milestone =>
                    milestone.status !==
                    PaymentMilestoneStatus.Cancelled
            ),
        [milestones]
    );

    const activeMilestoneTotal = useMemo(
        () =>
            fromMinorUnits(
                activeMilestones.reduce(
                    (total, milestone) =>
                        total +
                        toMinorUnits(milestone.amount),
                    0
                )
            ),
        [activeMilestones]
    );

    const totalPaid = useMemo(
        () =>
            fromMinorUnits(
                activeMilestones.reduce(
                    (total, milestone) =>
                        total +
                        toMinorUnits(
                            milestone.amountPaid
                        ),
                    0
                )
            ),
        [activeMilestones]
    );

    const revisedScheduleTotal = useMemo(
        () =>
            balanceMode ===
                PaymentScheduleBalanceMode
                    .RecalculateFromMilestones
                ? activeMilestoneTotal
                : money(enteredTotalAmount),
        [
            activeMilestoneTotal,
            balanceMode,
            enteredTotalAmount,
        ]
    );

    const totalOutstanding = useMemo(
        () =>
            money(
                revisedScheduleTotal - totalPaid
            ),
        [
            revisedScheduleTotal,
            totalPaid,
        ]
    );

    const balanceDifference = useMemo(
        () =>
            money(
                activeMilestoneTotal -
                revisedScheduleTotal
            ),
        [
            activeMilestoneTotal,
            revisedScheduleTotal,
        ]
    );

    const totalChanged = useMemo(
        () =>
            schedule !== null &&
            toMinorUnits(schedule.totalAmount) !==
            toMinorUnits(revisedScheduleTotal),
        [
            revisedScheduleTotal,
            schedule,
        ]
    );

    const cancelledMilestoneCount = useMemo(
        () =>
            milestones.filter(
                milestone =>
                    milestone.originalStatus !==
                    PaymentMilestoneStatus.Cancelled &&
                    milestone.status ===
                    PaymentMilestoneStatus.Cancelled
            ).length,
        [milestones]
    );

    function setMilestoneField(
        index: number,
        patch: Partial<EditableMilestoneRow>
    ) {
        setMilestones(previous =>
            previous.map((milestone, currentIndex) =>
                currentIndex === index
                    ? {
                        ...milestone,
                        ...patch,
                    }
                    : milestone
            )
        );
    }

    function resequenceActiveRows(
        rows: EditableMilestoneRow[]
    ): EditableMilestoneRow[] {
        let sequence = 1;

        return rows.map(row => {
            if (
                row.status ===
                PaymentMilestoneStatus.Cancelled
            ) {
                return row;
            }

            return {
                ...row,
                sequenceNumber: sequence++,
            };
        });
    }

    function addMilestone() {
        setMilestones(previous => {
            const activeRows = previous.filter(
                milestone =>
                    milestone.status !==
                    PaymentMilestoneStatus.Cancelled
            );

            const lastActive =
                activeRows[activeRows.length - 1];

            const nextSequence =
                activeRows.length + 1;

            const nextRow: EditableMilestoneRow = {
                milestoneId: null,
                diaryItemId: null,

                sequenceNumber: nextSequence,

                label: `Payment ${nextSequence}`,
                dueLocal:
                    lastActive?.dueLocal ??
                    new Date()
                        .toISOString()
                        .substring(0, 10),

                amount: 0,
                amountPaid: 0,

                originalStatus:
                    PaymentMilestoneStatus.Planned,

                status:
                    PaymentMilestoneStatus.Planned,

                canEditAmount: true,
                canEditDueDate: true,
                canDelete: true,
                canCancel: true,
                canRestore: false,

                rowVersion: null,

                isNew: true,
            };

            return [
                ...previous,
                nextRow,
            ];
        });
    }

    function cancelOrRemoveMilestone(index: number) {
        setMilestones(previous => {
            const selected = previous[index];

            if (!selected) return previous;

            if (selected.isNew) {
                return resequenceActiveRows(
                    previous.filter(
                        (_, currentIndex) =>
                            currentIndex !== index
                    )
                );
            }

            return resequenceActiveRows(
                previous.map(
                    (milestone, currentIndex) =>
                        currentIndex === index
                            ? {
                                ...milestone,
                                status:
                                    PaymentMilestoneStatus
                                        .Cancelled,
                            }
                            : milestone
                )
            );
        });
    }

    function restoreMilestone(index: number) {
        setMilestones(previous =>
            resequenceActiveRows(
                previous.map(
                    (milestone, currentIndex) =>
                        currentIndex === index
                            ? {
                                ...milestone,
                                status:
                                    PaymentMilestoneStatus
                                        .Planned,
                            }
                            : milestone
                )
            )
        );
    }

    function validate(): string[] {
        const errors: string[] = [];

        if (!title.trim()) {
            errors.push(
                "Schedule title is required."
            );
        }

        if (!currencyCode.trim()) {
            errors.push(
                "Currency is required."
            );
        }

        if (
            currencyCode.trim().length !== 3
        ) {
            errors.push(
                "Currency must contain three characters."
            );
        }

        if (
            toMinorUnits(revisedScheduleTotal) <
            toMinorUnits(totalPaid)
        ) {
            errors.push(
                "The schedule total cannot be lower than the amount already paid."
            );
        }

        if (
            balanceMode ===
            PaymentScheduleBalanceMode
                .UseEnteredScheduleTotal &&
            toMinorUnits(activeMilestoneTotal) !==
            toMinorUnits(
                revisedScheduleTotal
            )
        ) {
            errors.push(
                "Active milestone amounts must equal the entered schedule total."
            );
        }

        if (activeMilestones.length === 0) {
            if (
                toMinorUnits(
                    revisedScheduleTotal
                ) !== 0
            ) {
                errors.push(
                    "A non-zero schedule must contain at least one active milestone."
                );
            }
        }

        activeMilestones.forEach(
            (milestone, index) => {
                const name =
                    milestone.label.trim() ||
                    `Milestone ${index + 1}`;

                if (!milestone.dueLocal) {
                    errors.push(
                        `${name} requires a due date.`
                    );
                }

                if (
                    toMinorUnits(
                        milestone.amount
                    ) <= 0
                ) {
                    errors.push(
                        `${name} must have an amount greater than zero.`
                    );
                }

                if (
                    toMinorUnits(
                        milestone.amount
                    ) <
                    toMinorUnits(
                        milestone.amountPaid
                    )
                ) {
                    errors.push(
                        `${name} cannot be lower than the amount already paid.`
                    );
                }
            }
        );

        return errors;
    }

    function buildUpdateDto():
        PaymentScheduleUpdateDto {
        if (!schedule) {
            throw new Error(
                "The payment schedule has not been loaded."
            );
        }

        return {
            paymentScheduleId:
                schedule.paymentScheduleId,

            artworkId: artworkId,

            title: title.trim(),
            description: null,

            currencyCode:
                currencyCode
                    .trim()
                    .toUpperCase(),

            totalAmount:
                revisedScheduleTotal,

            balanceMode,

            visibility,

            milestones: milestones.map(
                milestone => ({
                    milestoneId:
                        milestone.milestoneId,

                    diaryItemId:
                        milestone.diaryItemId,

                    sequenceNumber:
                        milestone.sequenceNumber,

                    label:
                        milestone.label.trim() ||
                        null,

                    dueUtc:
                        toUtcIsoFromLocalDate(
                            milestone.dueLocal
                        ),

                    amount:
                        money(milestone.amount),

                    status:
                        milestone.status,

                    rowVersion:
                        milestone.rowVersion,
                })
            ),

            reminderOffsetsMinutes:
                reminders,

            rowVersion:
                schedule.rowVersion,
        };
    }

    async function handleSubmit() {
        if (!schedule) return;

        const errors = validate();

        if (errors.length > 0) {
            setSaveErrors(errors);
            return;
        }

        const changeMessages: string[] = [];

        if (totalChanged) {
            changeMessages.push(
                `The schedule total will change from ` +
                `${schedule.totalAmount.toFixed(2)} ` +
                `${schedule.currencyCode} to ` +
                `${revisedScheduleTotal.toFixed(2)} ` +
                `${currencyCode}.`
            );
        }

        if (cancelledMilestoneCount > 0) {
            changeMessages.push(
                `${cancelledMilestoneCount} milestone` +
                `${cancelledMilestoneCount === 1 ? "" : "s"} ` +
                `will be cancelled.`
            );
        }

        if (changeMessages.length > 0) {
            const confirmed = window.confirm(
                `${changeMessages.join("\n\n")}` +
                `\n\nPaid amounts will not be changed.`
            );

            if (!confirmed) return;
        }

        setSaving(true);
        setSaveErrors([]);

        try {
            const token =
                await getAccessToken();

            if (!token) {
                throw new Error(
                    "A valid access token could not be obtained."
                );
            }

            const dto = buildUpdateDto();

            const result = await putJson(
                `payment-schedules/${schedule.paymentScheduleId}`,
                token,
                JSON.stringify(dto)
            ) as PaymentScheduleSummaryDto;

            onUpdated(result);
            onClose();
        } catch (error) {
            setSaveErrors([
                extractErrorMessage(error),
            ]);
        } finally {
            setSaving(false);
        }
    }

    const validationErrors = useMemo(
        () => validate(),
        [
            activeMilestoneTotal,
            activeMilestones,
            balanceMode,
            currencyCode,
            revisedScheduleTotal,
            title,
            totalPaid,
        ]
    );

    const canSave =
        !loading &&
        !saving &&
        schedule !== null &&
        validationErrors.length === 0;

    return (
        <Dialog
            open={open}
            onClose={
                saving
                    ? undefined
                    : onClose
            }
            fullWidth
            maxWidth="lg"
        >
            <DialogTitle>
                Edit payment schedule
            </DialogTitle>

            <DialogContent dividers>
                {loading && (
                    <Box
                        sx={{
                            minHeight: 240,
                            display: "flex",
                            justifyContent:
                                "center",
                            alignItems: "center",
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}

                {!loading && loadError && (
                    <Alert severity="error">
                        {loadError}
                    </Alert>
                )}

                {!loading &&
                    !loadError &&
                    schedule && (
                        <Stack
                            spacing={2.5}
                            sx={{ mt: 1 }}
                        >
                            {saveErrors.length > 0 && (
                                <Alert severity="error">
                                    <Stack spacing={0.5}>
                                        {saveErrors.map(
                                            (
                                                error,
                                                index
                                            ) => (
                                                <Typography
                                                    key={
                                                        index
                                                    }
                                                    variant="body2"
                                                >
                                                    {error}
                                                </Typography>
                                            )
                                        )}
                                    </Stack>
                                </Alert>
                            )}

                            <TextField
                                label="Schedule title"
                                value={title}
                                onChange={event =>
                                    setTitle(
                                        event.target.value
                                    )
                                }
                                fullWidth
                            />

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        sm: "2fr 1fr",
                                    },
                                    gap: 2,
                                }}
                            >
                                <TextField
                                    label="Schedule total"
                                    type="number"
                                    value={
                                        balanceMode ===
                                            PaymentScheduleBalanceMode
                                                .RecalculateFromMilestones
                                            ? activeMilestoneTotal
                                            : enteredTotalAmount
                                    }
                                    onChange={event =>
                                        setEnteredTotalAmount(
                                            Number(
                                                event.target
                                                    .value || 0
                                            )
                                        )
                                    }
                                    disabled={
                                        balanceMode ===
                                        PaymentScheduleBalanceMode
                                            .RecalculateFromMilestones
                                    }
                                    inputProps={{
                                        min: 0,
                                        step: "0.01",
                                    }}
                                    fullWidth
                                />

                                <TextField
                                    label="Currency"
                                    value={currencyCode}
                                    onChange={event =>
                                        setCurrencyCode(
                                            event.target.value
                                                .toUpperCase()
                                        )
                                    }
                                    disabled={
                                        !schedule
                                            .canChangeCurrency
                                    }
                                    inputProps={{
                                        maxLength: 3,
                                    }}
                                    helperText={
                                        schedule.canChangeCurrency
                                            ? undefined
                                            : "Currency is locked because payments have been recorded."
                                    }
                                    fullWidth
                                />
                            </Box>

                            <FormControl>
                                <FormLabel>
                                    Schedule total handling
                                </FormLabel>

                                <RadioGroup
                                    value={balanceMode}
                                    onChange={event =>
                                        setBalanceMode(
                                            Number(
                                                event.target
                                                    .value
                                            ) as
                                            PaymentScheduleBalanceMode
                                        )
                                    }
                                >
                                    <FormControlLabel
                                        value={
                                            PaymentScheduleBalanceMode
                                                .UseEnteredScheduleTotal
                                        }
                                        control={
                                            <Radio />
                                        }
                                        label="Use entered schedule total"
                                    />

                                    <FormControlLabel
                                        value={
                                            PaymentScheduleBalanceMode
                                                .RecalculateFromMilestones
                                        }
                                        control={
                                            <Radio />
                                        }
                                        label="Set schedule total from active milestones"
                                    />
                                </RadioGroup>
                            </FormControl>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr 1fr",
                                        md: "repeat(5, minmax(0, 1fr))",
                                    },
                                    gap: 1.5,
                                }}
                            >
                                <SummaryValue
                                    label="Original total"
                                    value={
                                        schedule.totalAmount
                                    }
                                    currency={
                                        schedule.currencyCode
                                    }
                                />

                                <SummaryValue
                                    label="Revised total"
                                    value={
                                        revisedScheduleTotal
                                    }
                                    currency={
                                        currencyCode
                                    }
                                />

                                <SummaryValue
                                    label="Paid"
                                    value={totalPaid}
                                    currency={
                                        currencyCode
                                    }
                                />

                                <SummaryValue
                                    label="Outstanding"
                                    value={
                                        totalOutstanding
                                    }
                                    currency={
                                        currencyCode
                                    }
                                />

                                <SummaryValue
                                    label="Difference"
                                    value={
                                        balanceDifference
                                    }
                                    currency={
                                        currencyCode
                                    }
                                    highlight={
                                        toMinorUnits(
                                            balanceDifference
                                        ) !== 0
                                    }
                                />
                            </Box>

                            {balanceMode ===
                                PaymentScheduleBalanceMode
                                    .UseEnteredScheduleTotal &&
                                toMinorUnits(
                                    balanceDifference
                                ) !== 0 && (
                                    <Alert severity="warning">
                                        Active milestone
                                        amounts differ from the
                                        schedule total by{" "}
                                        {balanceDifference.toFixed(
                                            2
                                        )}{" "}
                                        {currencyCode}.
                                    </Alert>
                                )}

                            <Divider />

                            <Stack
                                direction={{
                                    xs: "column",
                                    sm: "row",
                                }}
                                justifyContent="space-between"
                                alignItems={{
                                    xs: "stretch",
                                    sm: "center",
                                }}
                                spacing={1}
                            >
                                <Box>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontWeight: 700,
                                        }}
                                    >
                                        Payment milestones
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Paid values are
                                        protected. Planned
                                        milestones may be
                                        amended or cancelled.
                                    </Typography>
                                </Box>

                                <Button
                                    startIcon={<AddIcon />}
                                    variant="outlined"
                                    onClick={addMilestone}
                                >
                                    Add milestone
                                </Button>
                            </Stack>

                            <Stack spacing={1.5}>
                                {milestones.map(
                                    (
                                        milestone,
                                        index
                                    ) => (
                                        <MilestoneEditorRow
                                            key={
                                                milestone
                                                    .milestoneId ??
                                                `new-${index}`
                                            }
                                            milestone={
                                                milestone
                                            }
                                            currencyCode={
                                                currencyCode
                                            }
                                            onChange={patch =>
                                                setMilestoneField(
                                                    index,
                                                    patch
                                                )
                                            }
                                            onCancelOrRemove={() =>
                                                cancelOrRemoveMilestone(
                                                    index
                                                )
                                            }
                                            onRestore={() =>
                                                restoreMilestone(
                                                    index
                                                )
                                            }
                                        />
                                    )
                                )}
                            </Stack>

                            <Divider />

                            <TimeOffsetCheckboxGroup
                                value={reminders}
                                onChange={setReminders}
                                label="Reminder offsets"
                            />
                        </Stack>
                    )}
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={onClose}
                    disabled={saving}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!canSave}
                    startIcon={
                        saving
                            ? (
                                <CircularProgress
                                    size={18}
                                />
                            )
                            : undefined
                    }
                >
                    {saving
                        ? "Saving"
                        : "Save changes"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

type SummaryValueProps = {
    label: string;
    value: number;
    currency: string;
    highlight?: boolean;
};

function SummaryValue({
    label,
    value,
    currency,
    highlight = false,
}: SummaryValueProps) {
    return (
        <Box
            sx={{
                p: 1.5,
                border: "1px solid",
                borderColor: highlight
                    ? "warning.main"
                    : "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
            }}
        >
            <Typography
                variant="caption"
                color="text.secondary"
            >
                {label}
            </Typography>

            <Typography
                variant="subtitle1"
                sx={{
                    fontWeight: 700,
                }}
            >
                {value.toLocaleString(
                    undefined,
                    {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    }
                )}{" "}
                {currency}
            </Typography>
        </Box>
    );
}

type MilestoneEditorRowProps = {
    milestone: EditableMilestoneRow;
    currencyCode: string;

    onChange: (
        patch: Partial<EditableMilestoneRow>
    ) => void;

    onCancelOrRemove: () => void;
    onRestore: () => void;
};

function MilestoneEditorRow({
    milestone,
    currencyCode,
    onChange,
    onCancelOrRemove,
    onRestore,
}: MilestoneEditorRowProps) {
    const isPaid =
        milestone.status ===
        PaymentMilestoneStatus.Paid;

    const isPartPaid =
        milestone.status ===
        PaymentMilestoneStatus.PartPaid;

    const isCancelled =
        milestone.status ===
        PaymentMilestoneStatus.Cancelled;

    const outstanding = money(
        Math.max(
            0,
            milestone.amount -
            milestone.amountPaid
        )
    );

    return (
        <Box
            sx={{
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: isCancelled
                    ? "action.disabledBackground"
                    : "background.paper",
                opacity: isCancelled
                    ? 0.8
                    : 1,
            }}
        >
            <Stack spacing={1.5}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 700,
                            }}
                        >
                            Payment{" "}
                            {
                                milestone.sequenceNumber
                            }
                        </Typography>

                        <Chip
                            label={getStatusLabel(
                                milestone.status
                            )}
                            color={getStatusChipColor(
                                milestone.status
                            )}
                            size="small"
                            variant={
                                isCancelled
                                    ? "outlined"
                                    : "filled"
                            }
                        />

                        {isPaid && (
                            <Tooltip title="Paid milestones are protected">
                                <LockOutlinedIcon
                                    fontSize="small"
                                    color="action"
                                />
                            </Tooltip>
                        )}
                    </Stack>

                    <Stack
                        direction="row"
                        spacing={0.5}
                    >
                        {isCancelled &&
                            milestone.canRestore && (
                                <Tooltip title="Restore milestone">
                                    <IconButton
                                        onClick={
                                            onRestore
                                        }
                                        size="small"
                                    >
                                        <RestoreOutlinedIcon />
                                    </IconButton>
                                </Tooltip>
                            )}

                        {!isCancelled &&
                            milestone.isNew && (
                                <Tooltip title="Remove milestone">
                                    <IconButton
                                        onClick={
                                            onCancelOrRemove
                                        }
                                        size="small"
                                    >
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </Tooltip>
                            )}

                        {!isCancelled &&
                            !milestone.isNew &&
                            milestone.canCancel && (
                                <Tooltip title="Cancel milestone">
                                    <IconButton
                                        onClick={
                                            onCancelOrRemove
                                        }
                                        size="small"
                                    >
                                        <CancelOutlinedIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                    </Stack>
                </Stack>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md:
                                "minmax(0, 1fr) 180px 170px",
                        },
                        gap: 1.5,
                    }}
                >
                    <TextField
                        label="Label"
                        value={milestone.label}
                        onChange={event =>
                            onChange({
                                label:
                                    event.target.value,
                            })
                        }
                        disabled={isCancelled}
                        fullWidth
                        size="small"
                    />

                    <TextField
                        label="Due date"
                        type="date"
                        value={
                            milestone.dueLocal
                        }
                        onChange={event =>
                            onChange({
                                dueLocal:
                                    event.target.value,
                            })
                        }
                        disabled={
                            isCancelled ||
                            !milestone
                                .canEditDueDate
                        }
                        InputLabelProps={{
                            shrink: true,
                        }}
                        fullWidth
                        size="small"
                    />

                    <TextField
                        label="Amount"
                        type="number"
                        value={milestone.amount}
                        onChange={event =>
                            onChange({
                                amount: Number(
                                    event.target
                                        .value || 0
                                ),
                            })
                        }
                        disabled={
                            isCancelled ||
                            !milestone
                                .canEditAmount
                        }
                        inputProps={{
                            min:
                                milestone.amountPaid,
                            step: "0.01",
                        }}
                        helperText={
                            milestone.amountPaid > 0
                                ? `Minimum ${milestone.amountPaid.toFixed(2)}`
                                : undefined
                        }
                        fullWidth
                        size="small"
                    />
                </Box>

                {(isPartPaid || isPaid) && (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr 1fr",
                                sm:
                                    "repeat(3, minmax(0, 1fr))",
                            },
                            gap: 1,
                        }}
                    >
                        <PaymentValue
                            label="Milestone"
                            value={
                                milestone.amount
                            }
                            currency={
                                currencyCode
                            }
                        />

                        <PaymentValue
                            label="Paid"
                            value={
                                milestone.amountPaid
                            }
                            currency={
                                currencyCode
                            }
                        />

                        <PaymentValue
                            label="Outstanding"
                            value={
                                outstanding
                            }
                            currency={
                                currencyCode
                            }
                        />
                    </Box>
                )}
            </Stack>
        </Box>
    );
}

type PaymentValueProps = {
    label: string;
    value: number;
    currency: string;
};

function PaymentValue({
    label,
    value,
    currency,
}: PaymentValueProps) {
    return (
        <Box>
            <Typography
                variant="caption"
                color="text.secondary"
            >
                {label}
            </Typography>

            <Typography variant="body2">
                {value.toLocaleString(
                    undefined,
                    {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    }
                )}{" "}
                {currency}
            </Typography>
        </Box>
    );
}